<template>
  <div
    class="dropdown"
    :class="{ dropdown_single: single }"
    v-click-outside="blur"
  >
    <div class="dropdown__preheader" v-if="preheader">
      {{ preheader }}<span class="form__required" v-if="required">*</span>
    </div>

    <button
      type="button"
      class="dropdown__header"
      :class="{ dropdown__header_disabled: disabled }"
      @click="onHeaderClick"
    >
      <span
        class="dropdown__header-text"
        :class="{ 'dropdown__header-text_opacity': isPlaceholderSameAsTitle }"
      >
        {{ firstWord }}
        <template v-if="modified">
          {{
            selectedOption.i18n
              ? $t(selectedOption[labelName])
              : selectedOption[labelName] || $props.title
          }}
        </template>
        <template v-else>
          {{ placeholder }}
        </template>
      </span>
      <span class="dropdown__header-actions">
        <template v-if="selectedOptionsCount >= 2">
          {{ selectedOptionsCount }}
        </template>

        <icon name="select-arrow" :class="{ _active: isVisible }" />
      </span>
    </button>
    <span v-if="error" class="dropdown__error">
      {{ error }}
    </span>

    <simplebar
      class="dropdown__content"
      :class="{
        dropdown__content_width100: isContentWidth100,
        dropdown__content_from_bottom: contentPositionFromBottom
      }"
      v-if="isVisible"
    >
      <template v-if="!single">
        <div
          class="dropdown__content-item"
          v-for="option in options"
          :key="option[keyName]"
          :class="{ _active: option.selected }"
        >
          <Checkbox
            class="dropdown__content-item-checkbox"
            v-model="option.selected"
          >
            {{ option[labelName] }}
          </Checkbox>
        </div>
      </template>

      <template v-else>
        <button
          class="dropdown__content-item"
          v-for="(option, index) in options"
          :key="option[keyName]"
          :class="{ _active: option.selected }"
          @click="optionChosenByIndex(index)"
        >
          {{ option.i18n ? $t(option[labelName]) : option[labelName] }}
        </button>
      </template>
    </simplebar>
  </div>
</template>

<script>
import Checkbox from "@v2/shared/ui/components/CheckBox/CheckBox.vue";
import { i18n } from "@v2/shared/config";

export default {
  name: "Dropdown",

  components: {
    Checkbox
  },

  props: {
    value: {
      type: Array,
      default: () => []
    },

    preheader: {
      type: String,
      default: null
    },

    required: {
      type: Boolean,
      default: false
    },

    isContentWidth100: {
      type: Boolean,
      default: false
    },

    contentPositionFromBottom: {
      type: Boolean,
      default: false
    },

    title: {
      type: String,
      default: "Поле"
    },

    single: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    },

    labelName: {
      type: String,
      default: "label"
    },

    keyName: {
      type: String,
      default() {
        return "value";
      }
    },

    withoutOpacityTitle: {
      type: Boolean,
      default: false
    },
    firstWord: {
      type: String,
      default: ""
    },
    error: String,
    modified: Boolean
  },

  data() {
    return {
      options: this.$props.value,
      selectedOption: {},
      isVisible: false
    };
  },

  created() {
    this.selectedOption = this.options.find(o => o.selected) || {};
  },

  computed: {
    isPlaceholderSameAsTitle() {
      return (
        !this.withoutOpacityTitle && this.placeholder === this.$props.title
      );
    },

    selectedOptionsCount() {
      return this.options.filter(option => option.selected).length;
    },

    placeholder() {
      switch (this.selectedOptionsCount) {
        case 0:
          return this.$props.title;

        case 1:
          return this.options.find(option => option.selected)[this.labelName];

        default:
          return i18n.locale === "ru"
            ? "Выбрано несколько"
            : "Multiple selected";
      }
    }
  },

  methods: {
    toggleVisibility(visibility = !this.isVisible) {
      this.isVisible = visibility;
    },

    optionChosenByIndex(index) {
      this.options.map((option, i) => {
        if (i !== index) {
          option.selected = false;
        }

        return option;
      });

      this.options[index].selected = true;
      this.selectedOption = this.options[index];
      this.$emit("select", this.selectedOption);
      this.toggleVisibility(false);
    },

    blur() {
      this.toggleVisibility(false);
    },

    onHeaderClick() {
      if (this.disabled) {
        return;
      }
      this.toggleVisibility();
    }
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.$emit("input", this.options);
      }
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/media";

.dropdown {
  position: relative;
  z-index: 12;
  &__preheader {
    font-size: 12px;
    line-height: 1.33;
    color: #6d7179;
    margin-bottom: 8px;

    pointer-events: none;
  }

  &__header {
    font-size: 13px;

    width: 100%;
    min-width: 150px;
    height: 48px;

    cursor: pointer;

    border: 1px solid #cdced0;
    outline: none;

    border-radius: 2px;
    background-color: #ffffff;

    padding: 0 15px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    @include respond-down(md) {
      height: 40px;
      width: 236px;
      margin-bottom: 16px;
      margin-top: 16px;
    }

    &:hover {
      border-color: #a3a8b3;
    }

    &-text {
      left: 16px;

      text-align: left;
      white-space: nowrap;

      width: 100%;
      overflow: hidden;

      text-overflow: ellipsis;

      &_opacity {
        color: #cdced0;
      }
    }

    &-actions {
      display: flex;
      font-size: 14px;
      font-weight: 500;
      color: #0070d2;

      .svg-icon {
        width: 16px;
        height: 16px;

        flex-shrink: 0;
        margin-left: 10px;

        color: #0070d2;

        &._active {
          transform: rotate(180deg);
        }
      }
    }

    &_disabled {
      pointer-events: none;
      touch-action: none;

      background: rgba(241, 241, 241, 0.5);
      border-color: rgba(205, 206, 208, 0.5);

      color: #000;
    }

    &_disabled &-actions {
      filter: grayscale(1);
    }
  }

  &__error {
    display: flex;
    margin-top: 8px;
    font-size: 0.75rem;
    color: var(--red, #ea5330);
  }

  &_error &__header {
    border-color: #ea5330;
  }

  &_slim &__header {
    border: none;
    padding: 0;
  }

  &_grey &__header {
    &-text {
      color: #a3a8b3;
    }

    &-actions {
      .svg-icon {
        color: #a3a8b3;
      }
    }
  }

  &_black-arr &__header {
    &-actions {
      .svg-icon {
        color: #000000;
      }
    }
  }

  &__content {
    border-radius: 2px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
    max-height: 300px;
    position: absolute !important;
    bottom: 0;
    left: 0;
    transform: translateY(calc(100% + 8px));
    width: 400px;
    background: #fff;
    z-index: 3;
    &_width100 {
      width: 100%;
    }

    &_from_bottom {
      bottom: auto;
      top: 0;
      transform: translateY(calc(-100% - 8px));
    }

    &-item {
      transition: all 0.3s;
      background: #fff;
      cursor: pointer;
      &-checkbox {
        padding: 8px 16px;
        display: flex;
        width: 100%;
        box-sizing: border-box;
      }

      &:hover {
        background: #009ee3;
      }

      &:hover &-checkbox {
        color: #fff;
      }
    }
  }

  &_slim &__content {
    width: 280px;
    transform: translateY(95%);
  }

  &_single &__content {
    &-item {
      display: block;
      width: 100%;
      text-align: left;
      border: none;
      outline: none;
      padding: 16px 16px;

      &:hover,
      &._active {
        color: #fff;
      }

      &._active {
        background: #009ee3;
      }
    }
  }

  &_subexponent {
    .dropdown__header {
      background-color: transparent;
      height: 21px;
    }
    &_table {
      padding: 0 0 0 0;
      margin: 0 0 0 0;

      .dropdown__header {
        color: #6d7179;
        font-size: 14px;
        font-weight: 500;

        .svg-icon {
          color: #6d7179;
          width: 11px;
        }
      }
    }
  }

  @include respond-down(md) {
    &__content {
      width: 100%;
    }
  }
}
</style>
